import { useState } from 'react';

import { appColors, Tier, TIERS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { RESULT_PAGE_TOUR_IDS } from '@backrs_ui/grow-ui/src/pages/Result/tour/ResultPage.tour';

import { Popover, Typography, Theme, SxProps, Box, useMediaQuery } from '@mui/material';

import { EntrepreneurshipBox } from './EntrepreneurshipBox';
import { Lines } from './Lines';
import { MiddleTierBox } from './MiddleTierBox';
import { SemiSkilledBox } from './SemiSkilledBox';
import { TopTierBox } from './TopTierBox';
import { TradeBox } from './TradeBox';
import { UpperTierBox } from './UpperTierBox';

/** The path for this SVG was pulled from Figma */
const RoundedTriangle = () => (
  <svg
    width="22"
    height="25"
    viewBox="0 0 22 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={SVGTriangleStyle}
  >
    <path
      d="M1.75 10.3349C0.0833308 11.2972 0.0833333 13.7028 1.75 14.6651L17.5 23.7583C19.1667 24.7206 21.25 23.5178 21.25 21.5933L21.25 3.40673C21.25 1.48223 19.1667 0.279418 17.5 1.24167L1.75 10.3349Z"
      fill="black"
      stroke="black"
    />
  </svg>
);

const CAREER_PATH_DIAGRAM_STRINGS = appStrings.modules.results.diagram;

type CareerPathDiagramProps = Readonly<{
  minimumTrack: Tier;
  entrepreneurshipRecommended: boolean;
  popoverPosition?: string;
}>;

export function CareerPathDiagram({
  minimumTrack,
  entrepreneurshipRecommended,
}: CareerPathDiagramProps) {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverContent, setPopoverContent] = useState<string>('');
  const [hoveredElement, setHoveredElement] = useState<Tier | null>(null);
  const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);

  const tierOrder: Tier[] = [TIERS.SEMISKILLED, TIERS.TRADE, TIERS.MID, TIERS.UPPER, TIERS.TOP];

  const minimumIndex = tierOrder.indexOf(minimumTrack);

  const isAboveOrEqualMinimum = (track: Tier) => tierOrder.indexOf(track) >= minimumIndex;

  const handleClick = (event: React.MouseEvent<SVGElement, MouseEvent>, content: string) => {
    setPopoverContent(content);
    setAnchorEl(event.currentTarget as SVGElement);
    setIsPopoverOpen(true);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
  };

  return (
    <Box sx={svgContainer}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 677 539"
        style={svgStyle}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        id={RESULT_PAGE_TOUR_IDS.step3}
      >
        <Lines
          semiSkilledToTradesTechnical={isAboveOrEqualMinimum(TIERS.TRADE)}
          semiSkilledToMiddleTier={isAboveOrEqualMinimum(TIERS.MID)}
          semiSkilledToUpperTier={isAboveOrEqualMinimum(TIERS.UPPER)}
          semiSkilledToTopTier={isAboveOrEqualMinimum(TIERS.TOP)}
          entrepreneurshipToTradesTechnical={
            entrepreneurshipRecommended && isAboveOrEqualMinimum(TIERS.TRADE)
          }
          entrepreneurshipToMiddleTier={
            entrepreneurshipRecommended && isAboveOrEqualMinimum(TIERS.MID)
          }
          entrepreneurshipToUpperTier={
            entrepreneurshipRecommended && isAboveOrEqualMinimum(TIERS.UPPER)
          }
          entrepreneurshipToTopTier={
            entrepreneurshipRecommended && isAboveOrEqualMinimum(TIERS.TOP)
          }
        />
        <SemiSkilledBox
          isHighlighted={minimumTrack === TIERS.SEMISKILLED}
          onClick={(event) =>
            handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.SEMISKILLED].tooltip)
          }
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.SEMISKILLED}
        />
        <EntrepreneurshipBox
          isHighlighted={entrepreneurshipRecommended}
          onClick={(event) =>
            handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.ENTREPRENEURSHIP].tooltip)
          }
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.ENTREPRENEURSHIP}
        />
        <MiddleTierBox
          isHighlighted={isAboveOrEqualMinimum(TIERS.MID)}
          onClick={(event) => handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.MID].tooltip)}
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.MID}
        />
        <UpperTierBox
          isHighlighted={isAboveOrEqualMinimum(TIERS.UPPER)}
          onClick={(event) => handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.UPPER].tooltip)}
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.UPPER}
        />
        <TopTierBox
          isHighlighted={isAboveOrEqualMinimum(TIERS.TOP)}
          onClick={(event) => handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.TOP].tooltip)}
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.TOP}
        />
        <TradeBox
          isHighlighted={isAboveOrEqualMinimum(TIERS.TRADE)}
          onClick={(event) => handleClick(event, CAREER_PATH_DIAGRAM_STRINGS[TIERS.TRADE].tooltip)}
          setHoveredBox={(tier: Tier | null) => setHoveredElement(tier)}
          isHovered={hoveredElement === TIERS.TRADE}
        />
      </svg>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        onClick={handleClose}
        sx={popoverStyle}
        PaperProps={{
          sx: paperSx,
        }}
      >
        <>
          {isMobile ? null : <RoundedTriangle />}
          <Typography sx={popoverTextStyle}>{popoverContent}</Typography>
        </>
      </Popover>
    </Box>
  );
}

const popoverStyle: SxProps<Theme> = {
  marginLeft: '-34px', // width of the triangle plus the transform shift
} as const;

const popoverTextStyle: SxProps<Theme> = {
  fontSize: '11pt',
  lineHeight: '1.3',
  whiteSpace: 'pre-line',
} as const;

const paperSx: SxProps<Theme> = {
  fontSize: '10pt',
  lineHeight: '1.3',
  paddingX: 3,
  paddingY: 2,
  backgroundColor: appColors.neutral.black,
  color: appColors.neutral.white,
  borderRadius: 4,
  maxWidth: 300,
  whiteSpace: 'pre-wrap',
  position: 'relative',
  overflow: 'visible',
  marginLeft: '20px',
};

const SVGTriangleStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(50% - 12px)',
  right: '-15px',
  transform: 'rotate(180deg)',
} as const;

const svgContainer: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
  width: '90%',
  '@media (min-width: 800px)': {
    width: 600,
  },
  '@media (min-width: 900px)': {
    maxWidth: 677,
  },
};

const svgStyle: React.CSSProperties = {
  position: 'relative',
};
